/**
 * 销售工作台的展示辅助：阶段 / 待跟进的标签、进度百分比、几天没讲的文案。
 * 判定规则在服务端 advisor-stage.ts，这里只负责「说成人话」。纯函数，便于单测。
 */
import type { Attention, BookOverviewDto, BookStage, WorkbenchItemDto } from '../../../api/advisor';

export const STAGE_META: Record<BookStage, { label: string; color: string }> = {
  awaiting_payment: { label: '待付款', color: 'gold' },
  expired: { label: '已过期', color: 'default' },
  narrating: { label: '讲述中', color: 'blue' },
  awaiting_confirm: { label: '待定稿确认', color: 'orange' },
  finalized: { label: '已定稿', color: 'cyan' },
  print_ordered: { label: '已下印刷单', color: 'purple' },
  shipped: { label: '已发货', color: 'green' },
  refunded: { label: '已退款', color: 'red' },
};

/** 阶段下拉：按一本书从头到尾的先后排 */
export const STAGE_OPTIONS: BookStage[] = [
  'awaiting_payment',
  'narrating',
  'awaiting_confirm',
  'finalized',
  'print_ordered',
  'shipped',
  'expired',
  'refunded',
];

export const ATTENTION_META: Record<Attention, { label: string; hint: string; color: string }> = {
  pay_expiring: { label: '订单快过期', hint: '客户还没付款，订单 48 小时内到期', color: 'red' },
  confirm_pending: { label: '待客户确认定稿', hint: '已发起定稿确认，等客户点确认', color: 'orange' },
  print_pending: { label: '待下印刷单', hint: '客户已定稿，等你代下印刷单', color: 'cyan' },
  ship_pending: { label: '待发货', hint: '印刷单已下，等你录快递公司和单号', color: 'purple' },
  idle: { label: '该提醒讲述了', hint: '讲述中的书已经 3 天以上没讲', color: 'blue' },
  pay_expired: { label: '订单已过期', hint: '客户没付款、订单过期了，需要重新开单', color: 'default' },
};

/** 紧急程度顺序（与服务端一致） */
export const ATTENTION_ORDER: Attention[] = [
  'pay_expiring',
  'confirm_pending',
  'print_pending',
  'ship_pending',
  'idle',
  'pay_expired',
];

/** 「N 天没讲」下拉的可选值 */
export const IDLE_DAY_OPTIONS = [3, 7, 14, 30];

const DAY_MS = 24 * 60 * 60 * 1000;
const TZ_OFFSET_MS = 8 * 60 * 60 * 1000;

/** 距现在几个中国时区自然日（今天 = 0） */
function calendarDaysAgo(ms: number, now: number): number {
  const day = (t: number) => Math.floor((t + TZ_OFFSET_MS) / DAY_MS);
  return Math.max(0, day(now) - day(ms));
}

/** 最近讲述的说法：今天 / 昨天 / N 天前 / 没讲过 */
export function lastNarratedText(ms: number | null, now = Date.now()): string {
  if (ms === null) return '还没讲过';
  const days = calendarDaysAgo(ms, now);
  if (days === 0) return '今天';
  if (days === 1) return '昨天';
  return `${days} 天前`;
}

/** 进度条百分比；总数为 0 返回 0 */
export function progressPercent(progress: { discussed: number; total: number } | null): number {
  if (!progress || progress.total <= 0) return 0;
  return Math.min(100, Math.round((progress.discussed / progress.total) * 100));
}

/** 讲述人标签：「王秀英（母亲）」；没填返回空串 */
export function bookTitle(book: Pick<BookOverviewDto, 'narratorName' | 'narratorRelation'>): string {
  const n = book.narratorName?.trim();
  const r = book.narratorRelation?.trim();
  if (n && r) return `${n}（${r}）`;
  return n || r || '';
}

/** 工作台每条待办的一句话说明 */
export function workbenchDetail(item: WorkbenchItemDto, now = Date.now()): string {
  switch (item.attention) {
    case 'pay_expiring': {
      const hours = Math.max(0, Math.ceil((item.expiresAt - now) / (60 * 60 * 1000)));
      return hours >= 24 ? `${Math.floor(hours / 24)} 天后过期` : hours <= 1 ? '1 小时内过期' : `${hours} 小时后过期`;
    }
    case 'pay_expired':
      return `已过期 ${Math.max(1, Math.floor((now - item.expiresAt) / DAY_MS))} 天`;
    case 'confirm_pending':
      return '等客户点确认定稿';
    case 'print_pending':
      return '已定稿，去下印刷单';
    case 'ship_pending':
      return '印刷单已下，去录快递单号';
    case 'idle':
      return item.lastNarratedAt === null ? `付款后 ${item.idleDays ?? 0} 天还没开始讲` : `${item.idleDays ?? 0} 天没讲了`;
  }
}

/** 金额（分）→「¥1,999」；有小数才显示小数 */
export function yuanText(fen: number): string {
  const yuan = fen / 100;
  return `¥${yuan.toLocaleString('zh-CN', { minimumFractionDigits: fen % 100 === 0 ? 0 : 2, maximumFractionDigits: 2 })}`;
}
